import { API_BASE } from "@/lib/apiClient";

/**
 * Zentrale, prozessweite WebSocket-Verbindung für die gesamte React-App.
 *
 * Statt dass jede Seite/jeder Hook eine eigene WebSocket öffnet, registrieren
 * sich alle Interessenten (Listener) an genau *einer* Verbindung. Der Client
 * bildet die Vereinigung aller gewünschten Scopes und hält die Subscription
 * serverseitig aktuell. Eingehende Events werden lokal anhand von
 * Scope/Domain/Event gefiltert und an die passenden Listener verteilt.
 *
 * Eigenschaften:
 *  - genau eine WebSocket-Verbindung pro Browser-Tab,
 *  - automatische Wiederverbindung mit exponentiellem Backoff (max. 30 s),
 *  - Heartbeat (Ping) alle 25 s,
 *  - Pause/Resume anhand `visibilitychange`,
 *  - Fallback-Polling pro Listener (Sicherheitsnetz, wenn keine Events kommen),
 *  - beobachtbarer Verbindungsstatus (für eine kleine Status-Anzeige).
 */

export type RealtimeStatus = "connecting" | "open" | "closed";

export interface RealtimeEventPayload {
  event?: string;
  event_type?: string;
  domain?: string;
  action?: string;
  scopes?: string[];
  entity?: string;
  entity_id?: string;
  data?: Record<string, unknown>;
}

export interface RealtimeListenerOptions {
  scopes?: string[];
  domains?: string[];
  events?: string[];
  /** Polling-Intervall (ms) als Sicherheitsnetz. Default 180_000. */
  fallbackIntervalMs?: number;
  /** Wird aufgerufen, wenn ein passendes Event eingeht oder das Polling tickt. */
  onChange: (payload?: RealtimeEventPayload) => void;
}

const MAX_RECONNECT_DELAY_MS = 30_000;
const HEARTBEAT_INTERVAL_MS = 25_000;

function normalizeScope(scope: string): string {
  return String(scope || "")
    .trim()
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9:_*.-]/g, "");
}

function buildSocketUrl(): string {
  if (typeof window === "undefined") return "";
  if (API_BASE) {
    const u = new URL(API_BASE);
    const proto = u.protocol === "https:" ? "wss:" : "ws:";
    return `${proto}//${u.host}/ws`;
  }
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${window.location.host}/ws`;
}

interface InternalListener {
  id: number;
  scopes: Set<string>;
  domains: Set<string>;
  events: Set<string>;
  fallbackIntervalMs: number;
  onChange: (payload?: RealtimeEventPayload) => void;
  pollTimer: number | null;
}

class RealtimeClient {
  private ws: WebSocket | null = null;
  private listeners = new Map<number, InternalListener>();
  private statusListeners = new Set<(status: RealtimeStatus) => void>();
  private status: RealtimeStatus = "closed";
  private nextId = 1;
  private started = false;
  private cancelled = false;

  private reconnectAttempts = 0;
  private reconnectTimer: number | null = null;
  private heartbeatTimer: number | null = null;
  private lastSentSubscriptions = "";
  private sessionId: string | null = null;

  // ── öffentliche API ──────────────────────────────────────────────────────

  getSessionId(): string | null {
    return this.sessionId;
  }

  addListener(opts: RealtimeListenerOptions): () => void {
    const id = this.nextId++;
    const listener: InternalListener = {
      id,
      scopes: new Set((opts.scopes || []).map(normalizeScope).filter(Boolean)),
      domains: new Set((opts.domains || []).map(normalizeScope).filter(Boolean)),
      events: new Set((opts.events || []).map((e) => String(e || "").trim()).filter(Boolean)),
      fallbackIntervalMs: opts.fallbackIntervalMs ?? 180_000,
      onChange: opts.onChange,
      pollTimer: null,
    };
    this.listeners.set(id, listener);
    this.startPoll(listener);
    this.ensureStarted();
    this.syncSubscriptions();
    return () => this.removeListener(id);
  }

  onStatus(cb: (status: RealtimeStatus) => void): () => void {
    this.statusListeners.add(cb);
    cb(this.status);
    return () => {
      this.statusListeners.delete(cb);
    };
  }

  getStatus(): RealtimeStatus {
    return this.status;
  }

  // ── intern ────────────────────────────────────────────────────────────────

  private removeListener(id: number): void {
    const listener = this.listeners.get(id);
    if (!listener) return;
    this.stopPoll(listener);
    this.listeners.delete(id);
    this.syncSubscriptions();
  }

  private setStatus(status: RealtimeStatus): void {
    if (this.status === status) return;
    this.status = status;
    for (const cb of this.statusListeners) {
      try {
        cb(status);
      } catch {
        /* ignore */
      }
    }
  }

  private unionScopes(): string[] {
    const out = new Set<string>();
    for (const listener of this.listeners.values()) {
      for (const scope of listener.scopes) out.add(scope);
    }
    return [...out].sort();
  }

  private send(payload: unknown): boolean {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return false;
    try {
      this.ws.send(JSON.stringify(payload));
      return true;
    } catch {
      return false;
    }
  }

  private syncSubscriptions(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    const scopes = this.unionScopes();
    const key = scopes.join(",");
    if (key === this.lastSentSubscriptions) return;
    if (this.send({ type: "set_subscriptions", scopes })) {
      this.lastSentSubscriptions = key;
    }
  }

  private startPoll(listener: InternalListener): void {
    this.stopPoll(listener);
    listener.pollTimer = window.setInterval(() => {
      if (document.visibilityState === "hidden") return;
      try {
        listener.onChange();
      } catch {
        /* ignore */
      }
    }, listener.fallbackIntervalMs);
  }

  private stopPoll(listener: InternalListener): void {
    if (listener.pollTimer != null) {
      window.clearInterval(listener.pollTimer);
      listener.pollTimer = null;
    }
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer != null) {
      window.clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = window.setInterval(() => {
      this.send({ type: "ping" });
    }, HEARTBEAT_INTERVAL_MS);
  }

  private ensureStarted(): void {
    if (this.started) return;
    if (typeof window === "undefined") return;
    this.started = true;
    this.cancelled = false;
    document.addEventListener("visibilitychange", this.handleVisibility);
    this.connect();
  }

  private handleVisibility = (): void => {
    if (document.visibilityState === "visible") {
      this.connect();
    }
  };

  private dispatch(payload: RealtimeEventPayload): void {
    const eventName = String(payload.event || "").trim();
    const domain = String(payload.domain || "").trim().toLowerCase();
    const messageScopes = (payload.scopes || []).map(normalizeScope).filter(Boolean);

    for (const listener of this.listeners.values()) {
      if (listener.events.size && !listener.events.has("*") && !listener.events.has(eventName)) {
        continue;
      }
      if (listener.domains.size && !listener.domains.has("*") && !listener.domains.has(domain)) {
        continue;
      }
      if (listener.scopes.size) {
        const hit = messageScopes.some((s) => listener.scopes.has(s));
        if (!hit) continue;
      }
      try {
        listener.onChange(payload);
      } catch {
        /* ignore */
      }
    }
  }

  private connect(): void {
    if (this.cancelled) return;
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }
    const url = buildSocketUrl();
    if (!url) return;

    this.setStatus("connecting");
    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch (err) {
      console.warn("[realtimeClient] open failed:", err);
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;

    ws.onopen = () => {
      this.reconnectAttempts = 0;
      this.lastSentSubscriptions = "";
      this.setStatus("open");
      this.startHeartbeat();
      this.send({ type: "set_subscriptions", scopes: this.unionScopes() });
      this.lastSentSubscriptions = this.unionScopes().join(",");
    };
    ws.onmessage = (msg) => {
      try {
        const payload = JSON.parse(msg.data);
        if (!payload || typeof payload !== "object") return;
        const eventName = String((payload as RealtimeEventPayload).event || "");
        if (eventName === "realtime_connected") {
          const sid = (payload as { data?: { session_id?: string } })?.data?.session_id;
          this.sessionId = sid ? String(sid) : null;
          return;
        }
        if (
          eventName === "realtime_subscription_updated" ||
          eventName === "pong"
        ) {
          return;
        }
        this.dispatch(payload as RealtimeEventPayload);
      } catch {
        /* ignore */
      }
    };
    ws.onerror = () => {
      /* Browser loggt selbst */
    };
    ws.onclose = () => {
      this.stopHeartbeat();
      if (this.ws === ws) this.ws = null;
      this.sessionId = null;
      if (!this.cancelled) {
        this.setStatus("connecting");
        this.scheduleReconnect();
      } else {
        this.setStatus("closed");
      }
    };
  }

  private scheduleReconnect(): void {
    if (this.cancelled || this.reconnectTimer != null) return;
    if (this.listeners.size === 0) {
      // Niemand hört zu → Verbindung ruhen lassen.
      this.setStatus("closed");
      this.started = false;
      document.removeEventListener("visibilitychange", this.handleVisibility);
      return;
    }
    const delay = Math.min(1000 * Math.max(1, 2 ** this.reconnectAttempts), MAX_RECONNECT_DELAY_MS);
    this.reconnectAttempts += 1;
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      if (document.visibilityState === "visible") this.connect();
    }, delay);
  }
}

export const realtimeClient = new RealtimeClient();

export function getRealtimeSessionId(): string | null {
  return realtimeClient.getSessionId();
}
